import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PageLayout from "../components/PageLayout";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaFlask, FaChevronRight } from "react-icons/fa";
import { RiFlaskLine } from "react-icons/ri";
import IngredientAPI from "../api/ingredient";
import { useSearchParams } from "react-router-dom";
import FilterAndDisplay from "../components/FilterAndDisplay";
import SponsersAffiliationsSidebar from "../components/SponsersAffiliationsSidebar";
import IngredientCard from "../components/ingredients/IngredientCard";

export default function Ingredient() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ingredients, setIngredients] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        totalItems: 0,
        totalPages: 0
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');

    // Display format state
    const [displayFormat, setDisplayFormat] = useState('grid'); // 'grid', 'list'


    const observer = useRef();
    const lastItemRef = useCallback(node => {
        if (isLoading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setCurrentPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [isLoading, hasMore]);

    const alphabetData = useMemo(() => {
        const alphabet = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
        const groups = {};

        alphabet.forEach(letter => {
            if (letter === '#') {
                groups[letter] = ingredients.filter(item =>
                    !/^[A-Za-z]$/.test(item.name)
                );
            } else {
                groups[letter] = ingredients.filter(item =>
                    item.name.toUpperCase().startsWith(letter)
                );
            }
        });
        const validLetters = alphabet.filter(letter => groups[letter].length > 0);

        return {
            alphabet,
            groups,
            validLetters,
            totalItems: pagination.totalItems
        };
    }, [ingredients, pagination.totalItems]);

    const [activeLetter, setActiveLetter] = useState(() => {
        const letterFromUrl = searchParams.get('letter');
        if (letterFromUrl && alphabetData.groups[letterFromUrl]?.length > 0) {
            return letterFromUrl;
        }
        return alphabetData.validLetters[0] || 'A';
    });

    const clearAllFilters = () => {
        setDisplayFormat('grid');
    };

    const hasActiveFilters = displayFormat !== 'grid';

    const fetchIngredients = useCallback(async (letter, page) => {
        try {
            setIsLoading(true);
            setError(null);

            const data = await IngredientAPI.getByLetterPage(letter === '#' ? 'number' : letter, page);
            if (page === 1) {
                setIngredients(data.results || []);
            } else {
                setIngredients(prevItems => [...prevItems, ...(data.results || [])]);
            }
            setPagination(data.pagination || { page: 1, totalItems: 0, totalPages: 0 });
            setHasMore(data.pagination?.page < data.pagination?.totalPages);
        } catch (err) {
            setError('Failed to load ingredients. Please try again later.');
            console.error('Error fetching ingredients:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        setIngredients([]);
        setCurrentPage(1);
        setHasMore(true);
        fetchIngredients(activeLetter, 1);
    }, [activeLetter, fetchIngredients]);

    useEffect(() => {
        if (currentPage > 1) {
            fetchIngredients(activeLetter, currentPage);
        }
    }, [currentPage, activeLetter, fetchIngredients]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        params.set('letter', activeLetter);
        navigate({
            pathname: location.pathname,
            search: params.toString()
        }, { replace: true });
    }, [activeLetter, navigate, location.pathname]);

    const handleSearch = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            setError(null);
            const data = await IngredientAPI.search(searchQuery);
            setIngredients(data.results || []);
            setPagination(data.pagination || { page: 1, totalItems: 0, totalPages: 0 });
            setCurrentPage(1);
            setHasMore(false);
        } catch (err) {
            setError('Search failed. Please try again later.');
            console.error('Error searching ingredients:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const switchToLetter = (letter) => {
        const params = new URLSearchParams(location.search);
        params.set('letter', letter);
        navigate({
            pathname: location.pathname,
            search: params.toString()
        }, { replace: true });
        setActiveLetter(letter);
        setIngredients([]);
        setCurrentPage(1);
        setHasMore(true);
        fetchIngredients(letter, 1);
    };

    // Grid View Component
    const GridView = ({ items }) => (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 fade-in">
            {items.map((item, index) => {
                const isLastItem = index === items.length - 1;
                return (
                    <>
                    <IngredientCard
                        key={`ingredient-card-${item.uri}`}
                        item={item}
                        isLastItem={isLastItem}
                        lastItemRef={isLastItem ? lastItemRef : null}
                    />
                    {
                        index % 10 === 0 && index > 9 && (
                            <div className="block md:hidden">
                                <SponsersAffiliationsSidebar showRange={index/ 10 % 2 === 0 ? {start: 0, end: 4} : {start: 4, end: 8}} />
                            </div>
                        )
                    }
                    </>
                );
            })}
        </div>
    );

    // List View Component
    const ListView = ({ items }) => (
        <div className="space-y-2 fade-in">
            {items.map((item, index) => {
                const isLastItem = index === items.length - 1;
                return (
                    <Link
                        key={item._id}
                        ref={isLastItem ? lastItemRef : null}
                        to={`/ingredients/${item.uri}`}
                        className="flex items-center p-3 transition-all duration-200 border bg-white/40 backdrop-blur-sm border-white/60 group rounded-xl hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-600/30"
                    >
                        <div className="flex-shrink-0 mr-3">
                            <div className="flex items-center justify-center transition-colors rounded-full w-10 h-10 bg-emerald-300/20 text-emerald-600 group-hover:bg-emerald-100">
                                <FaFlask className="size-4" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium transition-colors text-16 text-secondary group-hover:text-emerald-700">
                                {item.name.replace("&amp;", "&")}
                            </div>
                            <div className="text-14 text-secondary/80">
                                Active Ingredient
                            </div>
                        </div>
                        <div className="flex-shrink-0 ml-4 transition-transform transform group-hover:translate-x-1">
                            <div className="p-2 rounded-full text-secondary/70 group-hover:text-emerald-600">
                                <FaChevronRight className="size-4" />
                            </div>
                        </div>
                    </Link>
                );
            })}
        </div>
    );

    if (error) {
        return (
            <PageLayout
                title="Pharmaceutical"
                titleHighlight="Ingredients"
                subtitle="Browse our comprehensive database of active pharmaceutical ingredients alphabetically"
                showSearch={true}
                pageTitle="Ingredients"
                initialType="ingredients"
                showGradient={true}
            >
                <div className="p-6 text-center border shadow-sm bg-white/20 backdrop-blur-sm border-white/40 rounded-xl">
                    <div className="mb-4 text-red-500">{error}</div>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 text-sm font-medium text-white rounded-md bg-emerald-600 hover:bg-emerald-600/90"
                    >
                        Try Again
                    </button>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title="Pharmaceutical"
            titleHighlight="Ingredients"
            subtitle="Browse our comprehensive database of active pharmaceutical ingredients alphabetically"
            showSearch={true}
            pageTitle="Ingredients"
            initialType="ingredients"
            showGradient={true}
            onSearch={handleSearch}
            searchValue={searchQuery}
            onSearchChange={(e) => setSearchQuery(e.target.value)}
        >
            {/* Alpha Navigation Bar - Fixed at top */}
            <div className="sticky z-10 pt-4 pb-1 top-8 max-md:backdrop-blur-md">
                {/* Desktop Layout (lg and up): Side-by-side Alphabet & Combine Ingredients */}
                <div className="relative hidden lg:block">
                    <div className="flex justify-center items-center gap-3">
                        <nav className="inline-flex p-1 border shadow-sm rounded-xl bg-white/40 backdrop-blur-sm border-white/60">
                            {alphabetData.alphabet.map(letter => {
                                const isActive = activeLetter === letter;
                                return (
                                    <button
                                        key={letter}
                                        onClick={() => switchToLetter(letter)}
                                        className={`size-6 relative flex items-center justify-center m-0.5 rounded-md text-14 font-text font-medium transition-all ${isActive
                                                ? "bg-emerald-600 text-white shadow-sm"
                                                : "text-secondary hover:bg-emerald-600/5"
                                            }`}
                                    >
                                        {letter}
                                    </button>
                                );
                            })}
                        </nav>
                        
                        <Link
                            to="/ingredients/search"
                            className="flex items-center gap-2 px-3 h-[36px] transition-all border rounded-xl bg-emerald-50/50 border-emerald-200/60 hover:bg-emerald-50 hover:shadow-sm group"
                        >
                            <div className="flex items-center justify-center flex-shrink-0 rounded-md size-6 bg-emerald-100 text-emerald-600">
                                <RiFlaskLine className="size-3.5" />
                            </div>
                            <span className="font-medium text-14 text-emerald-800 whitespace-nowrap">Combine Ingredients</span>
                            <FaChevronRight className="flex-shrink-0 size-3 text-emerald-400 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    </div>
                </div>

                {/* Mobile / Tablet Layout (below lg): Horizontal Scrollable Alphabet & Icon-only Combine Ingredients */}
                <div className="pb-1 lg:hidden">
                    <div className="flex items-center gap-2">
                        <div className="flex-1 overflow-x-auto hide-scrollbar py-1">
                            <div className="flex gap-1">
                                {alphabetData.alphabet.map(letter => (
                                    <button
                                        key={letter}
                                        onClick={() => switchToLetter(letter)}
                                        className={`flex-shrink-0 h-9 min-w-[2.25rem] px-2.5 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${activeLetter === letter
                                                ? "bg-emerald-600 text-white"
                                                : "bg-white/60 text-secondary hover:bg-emerald-600/10 border border-white/70"
                                            }`}
                                    >
                                        {letter}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <Link
                            to="/ingredients/search"
                            title="Combine Ingredients"
                            className="flex items-center justify-center flex-shrink-0 w-9 h-9 transition-all border rounded-lg bg-emerald-50/50 border-emerald-200/60 hover:bg-emerald-50 hover:shadow-sm text-emerald-600"
                        >
                            <RiFlaskLine className="size-4.5" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="mt-2">
                {isLoading && currentPage === 1 ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="w-12 h-12 border-t-2 border-b-2 rounded-full animate-spin border-emerald-600"></div>
                    </div>
                ) : (
                    <div className="flex gap-4">
                        {/* Sidebar */}
                        <FilterAndDisplay
                            mode="sidebar"
                            themeColor="emerald"
                            filterOptions={[]}
                            selectedCategories={[]}
                            selectedForms={[]}
                            displayFormat={displayFormat}
                            onDisplayFormatChange={setDisplayFormat}
                            onClearAllFilters={clearAllFilters}
                        >
                            <SponsersAffiliationsSidebar />
                        </FilterAndDisplay>

                        {/* Main Content */}
                        <div className="w-full md:w-3/4">
                            {/* Smart Card Grid */}
                            {alphabetData.groups[activeLetter].length > 0 ? (
                                <>
                                    {displayFormat === 'grid' && (
                                        <GridView items={alphabetData.groups[activeLetter]} />
                                    )}
                                    {displayFormat === 'list' && (
                                        <ListView items={alphabetData.groups[activeLetter]} />
                                    )}
                                </>
                            ) : (
                                <div className="p-6 text-center border shadow-sm bg-white/20 backdrop-blur-sm border-white/40 rounded-xl">
                                    <div className="inline-flex items-center justify-center w-16 h-16 mb-3 text-2xl font-bold rounded-full bg-white/30 text-secondary/70">
                                        {activeLetter}
                                    </div>
                                    <h3 className="text-base font-medium text-secondary">No ingredients found</h3>
                                    <p className="mt-1 text-sm text-secondary/80">
                                        {hasActiveFilters
                                            ? "Try adjusting your filters or try another letter from the navigation above"
                                            : "Try another letter from the navigation above"
                                        }
                                    </p>
                                    {hasActiveFilters && (
                                        <button
                                            onClick={clearAllFilters}
                                            className="mt-3 px-4 py-2 text-sm font-medium text-white rounded-md bg-emerald-600 hover:bg-emerald-600/90"
                                        >
                                            Clear All Filters
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Loading indicator for additional items */}
                            {isLoading && currentPage > 1 && (
                                <div className="flex items-center justify-center py-4">
                                    <div className="w-8 h-8 border-t-2 border-b-2 rounded-full animate-spin border-emerald-600"></div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

        </PageLayout>
    );
}
